/**
 * FundingDetailPage — /aides/{slug}  (V9.5)
 *
 * Page détail publique d'une aide. Affiche tous les champs structurés
 * (porteur, montants, dépenses, dates, contacts, documents, source),
 * avec bouton de partage social et badges "à vérifier" si la fiabilité
 * de la source est incertaine.
 *
 * Le serveur renvoie 404 si is_deleted / archived / unpublished
 * (cf. utils.public_filter — V9.4.1).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showError } from "@/lib/errors";
import { ShareButton } from "@/components/ShareButton";
import {
  HandCoins, Calendar, ExternalLink, Mail, Phone, FileText, MapPin,
  AlertTriangle, ArrowLeft, Banknote, Globe, ShieldCheck,
} from "lucide-react";

const RELIABILITY_LABEL = {
  officielle: { label: "Source officielle", tone: "default",   icon: ShieldCheck },
  presse:     { label: "Source presse",     tone: "secondary", icon: Globe },
  declaratif: { label: "Déclaratif",        tone: "outline",   icon: FileText },
  a_verifier: { label: "À vérifier",        tone: "destructive", icon: AlertTriangle },
};

const TERRITORY_LABEL = {
  polynesie_francaise: "Polynésie française",
  commune: "Commune", ile: "Île",
  national: "National", international: "International",
};

const PROVIDER_LABEL = {
  pays: "Pays", djs: "DJS", commune: "Commune", federation: "Fédération",
  copf: "COPF", sponsor: "Sponsor", prive: "Privé", association: "Association", autre: "Autre",
};

const formatXPF = (n) => {
  if (n == null || n === "") return null;
  return new Intl.NumberFormat("fr-FR").format(n) + " XPF";
};

const formatDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function FundingDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data } = await api.get(`/funding/${slug}`);
        if (!cancel) setItem(data);
      } catch (e) {
        if (!cancel) {
          if (e?.response?.status === 404) setNotFound(true);
          else showError(e, { prefix: "Aide :" });
        }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 space-y-4" data-testid="funding-detail-loading">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center" data-testid="funding-detail-404">
        <HandCoins className="h-12 w-12 mx-auto text-muted-foreground" />
        <h1 className="font-display text-2xl font-bold mt-3">Aide introuvable</h1>
        <p className="text-sm text-muted-foreground mt-1">Cette aide n'existe pas ou n'est plus disponible publiquement.</p>
        <Button asChild className="mt-4"><Link to="/aides-sportifs"><ArrowLeft className="h-4 w-4 mr-1.5" />Voir toutes les aides</Link></Button>
      </div>
    );
  }

  const deadline = item.deadline_date || item.deadline;
  const isExpired = deadline && new Date(deadline) < new Date();
  const amtMinMax = (item.amount_min != null || item.amount_max != null)
    ? `${formatXPF(item.amount_min) || "?"} — ${formatXPF(item.amount_max) || "?"}`
    : null;
  const reliab = RELIABILITY_LABEL[item.reliability_level || item.source_type];
  const showVerifyBadge = !item.reliability_level || ["a_verifier", "declaratif"].includes(item.reliability_level);

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="funding-detail-page">
      {/* Header */}
      <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-xs" data-testid="funding-detail-back">
        <Link to="/aides-sportifs"><ArrowLeft className="h-3.5 w-3.5 mr-1" />Toutes les aides</Link>
      </Button>

      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          {item.provider_type && <Badge variant="outline" className="text-[10px]">{PROVIDER_LABEL[item.provider_type] || item.provider_type}</Badge>}
          {item.status && <Badge variant={item.status === "active" ? "default" : "secondary"} className="text-[10px]">{item.status}</Badge>}
          {isExpired && <Badge variant="destructive" className="text-[10px]">Expirée</Badge>}
          {showVerifyBadge && <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" />À vérifier</Badge>}
          {item.pinned && <Badge className="text-[10px]">📌 Épinglée</Badge>}
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="funding-detail-title">{item.title}</h1>
        {(item.provider_name || item.organizer) && (
          <p className="text-sm text-muted-foreground">
            {item.provider_name || item.organizer}
            {item.device_name && item.device_name !== item.title && <span> — {item.device_name}</span>}
          </p>
        )}
      </div>

      {/* Short description */}
      {item.short_description && (
        <p className="text-base leading-relaxed text-foreground" data-testid="funding-detail-short">{item.short_description}</p>
      )}

      {/* Highlights */}
      <Card className="p-4 space-y-2.5" data-testid="funding-detail-highlights">
        {amtMinMax && <Info icon={Banknote} label="Montant">{amtMinMax}</Info>}
        {!amtMinMax && item.amount_text && <Info icon={Banknote} label="Montant">{item.amount_text}</Info>}
        {item.funding_rate && <Info icon={Banknote} label="Taux de prise en charge">{item.funding_rate}</Info>}
        {deadline && <Info icon={Calendar} label="Date limite de dépôt">{formatDate(deadline)} {isExpired && <span className="text-destructive ml-1">(passée)</span>}</Info>}
        {item.opening_date && <Info icon={Calendar} label="Ouverture">{formatDate(item.opening_date)}</Info>}
        {item.territory && <Info icon={MapPin} label="Territoire">{TERRITORY_LABEL[item.territory] || item.territory}{item.island && ` · ${item.island}`}{item.commune && ` · ${item.commune}`}</Info>}
        {(item.eligible_profiles || []).length > 0 && <Info icon={ShieldCheck} label="Bénéficiaires">{item.eligible_profiles.join(" · ")}</Info>}
        {(item.disciplines || []).length > 0 && <Info icon={HandCoins} label="Disciplines">{item.disciplines.join(" · ")}</Info>}
      </Card>

      {/* Long description */}
      {(item.long_description || item.description) && (
        <Card className="p-4" data-testid="funding-detail-long">
          <h2 className="font-display font-bold mb-2">À propos de ce dispositif</h2>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{item.long_description || item.description}</div>
        </Card>
      )}

      {/* Critères + dépenses */}
      {(item.eligibility_criteria || item.conditions || (item.expense_covered || []).length > 0) && (
        <Card className="p-4 space-y-3">
          {(item.eligibility_criteria || item.conditions) && (
            <div>
              <h3 className="font-display font-bold text-sm mb-1">Critères d'éligibilité</h3>
              <div className="text-sm whitespace-pre-wrap text-muted-foreground">{item.eligibility_criteria || item.conditions}</div>
            </div>
          )}
          {(item.expense_covered || []).length > 0 && (
            <div>
              <h3 className="font-display font-bold text-sm mb-1">Dépenses couvertes</h3>
              <div className="flex flex-wrap gap-1.5">
                {item.expense_covered.map(c => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}
              </div>
            </div>
          )}
          {(item.expense_excluded || []).length > 0 && (
            <div>
              <h3 className="font-display font-bold text-sm mb-1">Dépenses exclues</h3>
              <div className="flex flex-wrap gap-1.5">
                {item.expense_excluded.map(c => <Badge key={c} variant="outline" className="text-[10px] border-destructive/40 text-destructive">{c}</Badge>)}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Documents à fournir */}
      {(item.required_documents || []).length > 0 && (
        <Card className="p-4" data-testid="funding-detail-docs">
          <h3 className="font-display font-bold text-sm mb-2">Documents à fournir</h3>
          <ul className="text-sm space-y-1">
            {item.required_documents.map((d, i) => <li key={i} className="flex items-start gap-1.5"><FileText className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />{d}</li>)}
          </ul>
        </Card>
      )}

      {/* CTA + contacts */}
      <Card className="p-4 space-y-3" data-testid="funding-detail-cta">
        <div className="flex gap-2 flex-wrap">
          {item.application_url && (
            <Button asChild data-testid="funding-detail-apply">
              <a href={item.application_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1.5" />Candidater
              </a>
            </Button>
          )}
          {(item.official_url || item.url) && (
            <Button asChild variant="outline" data-testid="funding-detail-official">
              <a href={item.official_url || item.url} target="_blank" rel="noopener noreferrer">
                <Globe className="h-4 w-4 mr-1.5" />Page officielle
              </a>
            </Button>
          )}
          {item.documents_url && (
            <Button asChild variant="outline">
              <a href={item.documents_url} target="_blank" rel="noopener noreferrer">
                <FileText className="h-4 w-4 mr-1.5" />Cahier des charges
              </a>
            </Button>
          )}
          <ShareButton
            kind="funding"
            slug={item.slug}
            title={item.title}
            description={item.short_description || ""}
            data-testid="funding-detail-share"
          />
        </div>
        {(item.contact_email || item.contact_phone || item.contact_name) && (
          <div className="text-xs text-muted-foreground space-y-0.5 pt-2 border-t">
            {item.contact_name && <div>Contact : {item.contact_name}</div>}
            {item.contact_email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /><a href={`mailto:${item.contact_email}`} className="hover:underline">{item.contact_email}</a></div>}
            {item.contact_phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.contact_phone}</div>}
          </div>
        )}
      </Card>

      {/* Notes publiques */}
      {item.public_notes && (
        <Card className="p-3 bg-amber-50 border-amber-200" data-testid="funding-detail-notes">
          <p className="text-xs text-amber-800 whitespace-pre-wrap">{item.public_notes}</p>
        </Card>
      )}

      {/* Source / fiabilité */}
      <Card className="p-3 text-xs text-muted-foreground" data-testid="funding-detail-source">
        <div className="flex items-center gap-2 flex-wrap">
          {reliab && (
            <Badge variant={reliab.tone} className="text-[10px]">
              <reliab.icon className="h-3 w-3 mr-1" />{reliab.label}
            </Badge>
          )}
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[40ch] inline-flex items-center gap-1">
              <Globe className="h-3 w-3" />{item.source_title || item.source_url}
            </a>
          )}
          {item.last_checked_at && <span>· Vérifiée le {formatDate(item.last_checked_at)}</span>}
          {item.ai_extracted && <Badge variant="outline" className="text-[10px]">Initialement extraite par IA</Badge>}
        </div>
      </Card>
    </div>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-4 w-4 mt-0.5 text-[hsl(var(--aito-lagoon))] shrink-0" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-medium">{children}</div>
      </div>
    </div>
  );
}
